import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, MoreHorizontal, Eye, CalendarCheck, Clock, Gauge, Fuel } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import TablePagination from "../components/TablePagination";
import { fmtDate } from "../lib/format";

const EMPTY = { booking_id: "", start_datetime: "", end_datetime: "", odometer_start: "", odometer_end: "", notes: "" };

export default function Trips() {
  const { user } = useAuth();
  const isDriver = user?.role === "driver";
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    api.get("/trips").then((r) => setTrips(r.data));
    api.get("/bookings").then((r) => setBookings(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const loggable = bookings.filter((b) => b.status === "diluluskan" && !b.trip_logged);

  const calc = useMemo(() => {
    const dist = Number(form.odometer_end) - Number(form.odometer_start);
    if (form.odometer_end && form.odometer_start && dist > 0) {
      return { dist: dist.toFixed(1) };
    }
    return { dist: null };
  }, [form.odometer_start, form.odometer_end]);

  const selectBooking = (id) => {
    const b = bookings.find((x) => x.id === id);
    setForm({ ...form, booking_id: id, start_datetime: b?.start_datetime || "", end_datetime: b?.end_datetime || "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/trips", {
        ...form,
        odometer_start: Number(form.odometer_start),
        odometer_end: Number(form.odometer_end),
        fuel_liters: 0,
        fuel_cost_rm: 0,
      });
      if (data.kmpl) {
        toast.success(`Perjalanan direkod. Kadar: ${data.kmpl} Km/L`);
      } else {
        toast.success("Perjalanan direkod.");
      }
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const filtered = trips.filter((t) => {
    const q = search.toLowerCase();
    return !q || `${t.driver_name} ${t.vehicle_reg} ${t.destination}`.toLowerCase().includes(q);
  });
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="space-y-6 fade-up" data-testid="trips-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Log Perjalanan</h1>
          <p className="text-sm text-muted-foreground mt-1">Logbook pintar dengan pengiraan Km/Liter automatik — {trips.length} log direkodkan.</p>
        </div>
        {isDriver && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#10056E] hover:bg-[#2a17a8]" data-testid="log-trip-btn">
                <Plus className="h-4 w-4 mr-2" /> Log Perjalanan
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Rekod Perjalanan</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-5">
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-1.5">
                    <CalendarCheck className="h-3.5 w-3.5" /> Tempahan
                  </p>
                  <div className="space-y-1.5">
                    <Label>Tempahan Diluluskan <span className="text-[#DC3545]">*</span></Label>
                    <Select value={form.booking_id} onValueChange={selectBooking}>
                      <SelectTrigger data-testid="trip-booking-select"><SelectValue placeholder="Pilih tempahan" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {loggable.length === 0 && <SelectItem value="none" disabled>Tiada tempahan diluluskan</SelectItem>}
                        {loggable.map((b) => (
                          <SelectItem key={b.id} value={b.id}>{b.vehicle_reg} — {b.destination} ({fmtDate(b.start_datetime)})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.booking_id && (() => {
                    const b = bookings.find((x) => x.id === form.booking_id);
                    return b ? (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm" data-testid="trip-booking-summary">
                        <p className="font-semibold text-[#10056E]">{b.vehicle_reg} → {b.destination}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{b.purpose} · Anggaran {b.estimated_distance} km</p>
                      </div>
                    ) : null;
                  })()}
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" /> Masa Perjalanan
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Masa Mula <span className="text-[#DC3545]">*</span></Label>
                      <Input data-testid="trip-start-input" type="datetime-local" required value={form.start_datetime}
                        onChange={(e) => setForm({ ...form, start_datetime: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Masa Tamat <span className="text-[#DC3545]">*</span></Label>
                      <Input data-testid="trip-end-input" type="datetime-local" required value={form.end_datetime}
                        onChange={(e) => setForm({ ...form, end_datetime: e.target.value })} />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-1.5">
                    <Gauge className="h-3.5 w-3.5" /> Bacaan Odometer
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Odometer Mula (KM) <span className="text-[#DC3545]">*</span></Label>
                      <Input data-testid="trip-odo-start-input" type="number" min="0" step="0.1" required value={form.odometer_start}
                        onChange={(e) => setForm({ ...form, odometer_start: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Odometer Akhir (KM) <span className="text-[#DC3545]">*</span></Label>
                      <Input data-testid="trip-odo-end-input" type="number" min="0" step="0.1" required value={form.odometer_end}
                        onChange={(e) => setForm({ ...form, odometer_end: e.target.value })} />
                    </div>
                  </div>
                  {calc.dist && (
                    <div className="bg-slate-50 border rounded p-3 text-sm" data-testid="trip-auto-calc">
                      Jarak perjalanan: <b>{calc.dist} km</b>
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-2.5 bg-indigo-50 border border-indigo-200 rounded-lg p-3" data-testid="trip-fuel-info">
                  <Fuel className="h-4 w-4 text-[#10056E] shrink-0 mt-0.5" />
                  <p className="text-xs text-[#10056E]">
                    Isi minyak? Rekodkan melalui menu <b>Isi Minyak</b> berserta resit — kos bahan api akan
                    dipadankan secara automatik dengan tempahan ini dan Km/L dikira sendiri oleh sistem.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label>Catatan (pilihan)</Label>
                  <Textarea data-testid="trip-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <Button type="submit" className="w-full h-11 bg-[#10056E] hover:bg-[#2a17a8]" disabled={!form.booking_id} data-testid="trip-submit-btn">
                  Rekod Perjalanan
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex flex-wrap items-center justify-end gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Cari staf, kenderaan, destinasi..." value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="trip-search-input" />
          </div>
        </div>
        {/* Mobile card list */}
        <div className="sm:hidden divide-y" data-testid="trips-mobile-list">
          {paged.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">Tiada log perjalanan.</p>}
          {paged.map((t) => (
            <div key={t.id} className="p-4 active:bg-slate-50" onClick={() => navigate(`/log-perjalanan/${t.id}`)} data-testid={`trip-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate">{t.destination}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.vehicle_reg}{!isDriver ? ` · ${t.driver_name}` : ""}</p>
                </div>
                {t.trip_status === "berjalan" ? (
                  <Badge className="bg-sky-600 hover:bg-sky-600">Berjalan</Badge>
                ) : (
                  <Badge className="bg-[#10056E] hover:bg-[#10056E]">{t.kmpl ?? "-"} Km/L</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {fmtDate(t.start_datetime)} · {t.distance_km} km · {t.fuel_liters > 0 ? `${t.fuel_liters} L / RM ${Number(t.fuel_cost_rm || 0).toFixed(2)}` : "Tiada isi minyak"} · <b>{t.kmpl ?? "-"} Km/L</b>
              </p>
            </div>
          ))}
        </div>
        <div className="hidden sm:block">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/70">
              <TableHead>Tarikh</TableHead>
              {!isDriver && <TableHead>Staf</TableHead>}
              <TableHead>Kenderaan</TableHead>
              <TableHead>Destinasi</TableHead>
              <TableHead>Jarak (KM)</TableHead>
              <TableHead>Bahan Api</TableHead>
              <TableHead>Kos/KM</TableHead>
              <TableHead>Km/L</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Tindakan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-10">Tiada log perjalanan.</TableCell></TableRow>
            )}
            {paged.map((t) => (
              <TableRow key={t.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/log-perjalanan/${t.id}`)} data-testid={`trip-row-${t.id}`}>
                <TableCell className="text-sm">{fmtDate(t.start_datetime)}</TableCell>
                {!isDriver && <TableCell className="text-sm font-semibold">{t.driver_name}</TableCell>}
                <TableCell className="text-sm font-semibold">{t.vehicle_reg}</TableCell>
                <TableCell className="text-sm">{t.destination}</TableCell>
                <TableCell className="text-sm">{t.distance_km}</TableCell>
                <TableCell className="text-sm">{t.fuel_liters > 0 ? `${t.fuel_liters} L / RM ${Number(t.fuel_cost_rm).toFixed(2)}` : <span className="text-muted-foreground">-</span>}</TableCell>
                <TableCell className="text-sm">{t.distance_km > 0 && t.fuel_cost_rm > 0 ? `RM ${(t.fuel_cost_rm / t.distance_km).toFixed(2)}` : "-"}</TableCell>
                <TableCell className="text-sm font-semibold">{t.kmpl ?? "-"}</TableCell>
                <TableCell>
                  {t.trip_status === "berjalan" ? (
                    <Badge className="bg-sky-600 hover:bg-sky-600">Berjalan</Badge>
                  ) : (
                    <Badge className="bg-[#198754] hover:bg-[#198754]">Selesai</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`trip-actions-${t.id}`}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white w-44">
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate(`/log-perjalanan/${t.id}`)}>
                        <Eye className="h-4 w-4 mr-2" /> Lihat Detail
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
        <TablePagination page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} total={filtered.length} />
      </div>
    </div>
  );
}
